import { Box, Button, IconButton, Text, useToast } from "@chakra-ui/react";
import {
  type Account,
  type LedgerAccount,
  type MnemonicAccount,
  type SecretKeyAccount,
  type SocialAccount,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { useGetSecretKey } from "@umami/state";
import { isMultichainEnabled } from "@umami/utils";

import { RemoveAccountModal } from "./RemoveAccountModal";
import { RenameAccountPage } from "./RenameAccountModal";
import {
  ArrowDownLeftIcon,
  EditIcon,
  EyeIcon,
  SearchIcon,
  ThreeDotsIcon,
  TrashIcon,
} from "../../assets/icons";
import { usePasskey } from "../../hooks/usePasskey";
import { useColor } from "../../styles/useColor";
import { ActionsDropdown } from "../ActionsDropdown";
import { RevealSeedPhraseModal } from "./RevealSeedPhraseModal";
import { getIsMnemonicAccountType, getIsSecretKeyAccountType } from "../../utils/accounts";
import { MasterPasswordModal } from "../MasterPasswordModal";
import { AccountInfoModal } from "./AccountInfoModal";
import { NetworkSelectorModal } from "../NetworkSelector/NetworkSelectorModal";

type AccountSelectorPopoverProps = {
  account: Account;
};

export const AccountSelectorPopover = ({ account }: AccountSelectorPopoverProps) => {
  const { openWith } = useDynamicModalContext();
  const color = useColor();
  const { isPasskeyFlow, authenticate, isLoading, isPasskeyUser } = usePasskey();
  const getSecret = useGetSecretKey();
  const toast = useToast();
  const isMnemonicAccountType = getIsMnemonicAccountType(account);
  const isSecretKeyAccountType = getIsSecretKeyAccountType(account);

  const canRevealSecret = isMnemonicAccountType || isSecretKeyAccountType;
  const revealButtonText = isMnemonicAccountType ? "Reveal Seed Phrase" : "Reveal Secret Key";

  const isEnabled = isMultichainEnabled();

  const handleRevealSecret = async () => {
    const revealSecret = async (password?: string) => {
      if (password) {
        try {
          const secrets = await getSecret(account as MnemonicAccount | SecretKeyAccount, password);
          const secret = isMnemonicAccountType ? (secrets.mnemonic as string) : secrets.secretKey;
          await openWith(<RevealSeedPhraseModal account={account} secret={secret} />);
        } catch (e: any) {
          toast({
            status: "error",
            description: e.message,
            id: "authentication-error",
            isClosable: true,
          });
        }
      }
    };

    if (isPasskeyFlow && isPasskeyUser) {
      const { dek: password } = await authenticate();
      await revealSecret(password);
    } else {
      await openWith(<MasterPasswordModal onSubmit={revealSecret} submitButtonText="Confirm" />);
    }
  };
  const actions = (
    <Box>
      <Button
        onClick={e => {
          e.stopPropagation();

          if (isEnabled) {
            return openWith(<NetworkSelectorModal account={account} />);
          } else {
            return openWith(<AccountInfoModal account={account} />);
          }
        }}
        variant="dropdownOption"
      >
        {isEnabled ? <ArrowDownLeftIcon /> : <SearchIcon />}
        <Text color={color("900")} fontWeight="600">
          {isEnabled ? "Receive" : "Account info"}
        </Text>
      </Button>
      {canRevealSecret && (
        <Button
          isLoading={isLoading}
          onClick={async e => {
            e.stopPropagation();
            await handleRevealSecret();
          }}
          variant="dropdownOption"
        >
          <EyeIcon />
          <Text color={color("900")} fontWeight="600">
            {revealButtonText}
          </Text>
        </Button>
      )}
      <Button
        onClick={e => {
          e.stopPropagation();

          return openWith(<RenameAccountPage account={account} />);
        }}
        variant="dropdownOption"
      >
        <EditIcon />
        <Text color={color("900")} fontWeight="600">
          Rename
        </Text>
      </Button>
      {account.type !== "secret_key" &&
        account.type !== "mnemonic" &&
        account.type !== "multisig" && (
          <Button
            onClick={e => {
              e.stopPropagation();

              return openWith(
                <RemoveAccountModal
                  account={account as SocialAccount | LedgerAccount | SecretKeyAccount}
                />
              );
            }}
            variant="dropdownOption"
          >
            <TrashIcon />
            <Text color={color("900")} fontWeight="600">
              Remove
            </Text>
          </Button>
        )}
    </Box>
  );

  return (
    <ActionsDropdown actions={actions}>
      <IconButton
        alignSelf="flex-end"
        color={color("500")}
        background="transparent"
        aria-label="Account actions"
        icon={<ThreeDotsIcon />}
        onClick={event => event.stopPropagation()}
        size="xs"
        variant="ghost"
      />
    </ActionsDropdown>
  );
};
